// Utility functions for date and currency formatting

/**
 * Format date to UK format (DD/MM/YYYY)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDateUK = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format date to ISO string for API (YYYY-MM-DD)
 * @param {Date} date - Date object
 * @returns {string} ISO date string
 */
export const formatDateISO = (date) => {
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse ISO date string to Date object
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @returns {Date} Date object
 */
export const parseISODate = (dateString) => {
  if (!dateString) return null;
  
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format currency to South African Rand (ZAR)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'R0.00';
  
  return `R${Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;
};

/**
 * Format currency with simple format (no decimals for whole numbers)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrencySimple = (amount) => {
  if (amount === null || amount === undefined) return 'R0';
  
  const num = Number(amount);
  if (num % 1 === 0) {
    return `R${num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;
  }
  return `R${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;
};

/**
 * Check if date is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (date) => {
  const dateObj = typeof date === 'string' ? parseISODate(date) : date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dateObj < today;
};

/**
 * Format date and time together
 * @param {string} date - Date string
 * @param {string} time - Time string
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date, time) => {
  return `${formatDateUK(date)} at ${time}`;
};
